import { ExpandMore } from '@mui/icons-material';
import { Breadcrumbs, IconButton, Link, Skeleton, Stack, Typography, TypographyOwnProps } from '@mui/material';
import { useEffect, useState } from 'react';
import { BreadcrumbItemType } from '../../types';

type Variant =
    | 'h1'
    | 'h2'
    | 'h3'
    | 'h4'
    | 'h5'
    | 'h6'
    | 'subtitle1'
    | 'subtitle2'
    | 'body1'
    | 'body2'
    | 'caption'
    | 'button'
    | 'overline';

const CustomBreadCrumbs = ({
    breadcrumbs = [],
    title,
    variant = 'h5',
    loading = false,
    onClick,
    onMenuClick,
    onRootClick,
    onAccessClick,
}: {
    breadcrumbs: BreadcrumbItemType[];
    title?: String;
    variant?: Variant;
    loading?: boolean;
    onClick?: (value: BreadcrumbItemType) => void;
    onMenuClick?: (event: React.MouseEvent<HTMLElement>, value: BreadcrumbItemType) => void;
    onRootClick?: () => void;
    onAccessClick?: (event: React.MouseEvent<HTMLElement>) => void;
}) => {
    const [isEmpty, setIsEmpty] = useState(true);

    useEffect(() => {
        setIsEmpty(!breadcrumbs.length);
    }, [breadcrumbs]);

    const handleRootClick = () => {
        onRootClick?.();
    };

    const handleClick = (breadcrumb: BreadcrumbItemType) => {
        onClick?.(breadcrumb);
    };

    const handleMenuClick = (event: React.MouseEvent<HTMLElement>, breadcrumb: BreadcrumbItemType) => {
        onMenuClick?.(event, breadcrumb);
    };

    const handleAccessClick = (event: React.MouseEvent<HTMLElement>) => {
        onAccessClick?.(event);
    };

    const getSkeleton = (index?: number) => (
        <Typography key={index} variant={variant}>
            <Skeleton width={120} />
        </Typography>
    );

    if (isEmpty) {
        return (
            <Breadcrumbs sx={{ minHeight: '36px' }}>
                <Stack direction="row">
                    <Typography variant={variant}>{title}</Typography>
                    {onAccessClick && (
                        <IconButton
                            size="small"
                            color="inherit"
                            onClick={(event: React.MouseEvent<HTMLElement>) => {
                                handleAccessClick(event);
                            }}
                        >
                            <ExpandMore />
                        </IconButton>
                    )}
                </Stack>
                {loading && getSkeleton()}
            </Breadcrumbs>
        );
    }

    if (loading) {
        return (
            <Breadcrumbs sx={{ minHeight: '36px' }}>
                <Typography variant={variant}>{title}</Typography>
                {breadcrumbs.map((_: BreadcrumbItemType, index: number) => getSkeleton(index))}
            </Breadcrumbs>
        );
    }

    return (
        <Breadcrumbs sx={{ minHeight: '36px' }}>
            <Stack direction="row">
                <Link href="#" onClick={() => handleRootClick()} variant={variant} underline="hover">
                    {title}
                </Link>
            </Stack>
            {breadcrumbs.map((breadcrumb: BreadcrumbItemType, index: number) => {
                if (index === breadcrumbs.length - 1) {
                    return (
                        <Stack key={index} direction="row">
                            <Typography variant={variant}>{breadcrumb.name}</Typography>
                            {onMenuClick && (
                                <IconButton
                                    size="small"
                                    color="inherit"
                                    onClick={(event: React.MouseEvent<HTMLElement>) => {
                                        handleMenuClick(event, breadcrumb);
                                    }}
                                >
                                    <ExpandMore />
                                </IconButton>
                            )}
                        </Stack>
                    );
                } else {
                    return (
                        <Link
                            key={index}
                            href="#"
                            onClick={() => handleClick(breadcrumb)}
                            variant={variant}
                            underline="hover"
                        >
                            {breadcrumb.name}
                        </Link>
                    );
                }
            })}
        </Breadcrumbs>
    );
};

export default CustomBreadCrumbs;
